/**
 * Image card slider — only when .bts-cards-with-image__cards.image-card-slider.
 * Requires Swiper (CDN / wp_enqueue). Otherwise cards use theme grid styles.
 * 1 slide mobile, 2 tablet (768+), 3 desktop (1025+).
 */

const SLIDER_SELECTOR = '.bts-cards-with-image__cards.image-card-slider';
const NAV_ICON = 'right-arrow-bts.svg';

function isSliderContainer( container ) {
	if ( container.closest( '.bts-artworks' ) ) {
		return false;
	}

	return (
		container.classList.contains( 'bts-cards-with-image__cards' ) &&
		container.classList.contains( 'image-card-slider' )
	);
}

const TABLET_MIN = 768;
const DESKTOP_MIN = 1025;

function getThemeImagesBase() {
	if ( window.breakTheSilenceTheme?.imagesUrl ) {
		return window.breakTheSilenceTheme.imagesUrl;
	}

	const sample = document.querySelector(
		'img[src*="/wp-content/themes/break-the-silence/assets/images/"]'
	);

	if ( sample ) {
		return sample.src.replace( /\/[^/]+$/, '/' );
	}

	return '/wp-content/themes/break-the-silence/assets/images/';
}

function createNavIcon( direction ) {
	const img = document.createElement( 'img' );
	img.src = `${ getThemeImagesBase() }${ NAV_ICON }`;
	img.alt = '';
	img.setAttribute( 'aria-hidden', 'true' );
	img.className = 'image-card-slider__nav-icon';
	img.width = 18;
	img.height = 15;

	if ( direction === 'prev' ) {
		img.classList.add( 'image-card-slider__nav-icon--prev' );
	}

	return img;
}

function createNavButton( direction, label ) {
	const button = document.createElement( 'button' );
	button.type = 'button';
	button.className = `swiper-button-${ direction } image-card-slider__nav image-card-slider__nav--${ direction }`;
	button.setAttribute( 'aria-label', label );
	button.appendChild( createNavIcon( direction ) );
	return button;
}

function ensureNavIcons( swiperEl ) {
	swiperEl
		.querySelectorAll( '.image-card-slider__nav' )
		.forEach( ( button ) => {
			if ( button.querySelector( '.image-card-slider__nav-icon' ) ) {
				return;
			}

			const direction = button.classList.contains(
				'image-card-slider__nav--prev'
			)
				? 'prev'
				: 'next';

			button.appendChild( createNavIcon( direction ) );
		} );
}

function wrapCardsAsSwiper( container ) {
	container.classList.add( 'swiper' );

	const wrapper = document.createElement( 'div' );
	wrapper.className = 'swiper-wrapper';

	const cards = Array.from(
		container.querySelectorAll(
			':scope > .bts-cards-with-image__card, :scope > article'
		)
	);

	cards.forEach( ( card ) => {
		const slide = document.createElement( 'div' );
		slide.className = 'swiper-slide';
		slide.appendChild( card );
		wrapper.appendChild( slide );
	} );

	container.replaceChildren( wrapper );
	container.appendChild(
		createNavButton( 'prev', 'Previous slide' )
	);
	container.appendChild( createNavButton( 'next', 'Next slide' ) );

	return container;
}

function getSwiperElement( container ) {
	if (
		container.classList.contains( 'swiper' ) &&
		container.querySelector( '.swiper-wrapper' )
	) {
		return container;
	}

	return container.querySelector( ':scope > .swiper' );
}

function createSwiper( swiperEl ) {
	const SwiperCtor = window.Swiper;
	if ( ! SwiperCtor ) {
		return null;
	}

	swiperEl.querySelectorAll( '.swiper-pagination' ).forEach( ( el ) => {
		el.remove();
	} );

	ensureNavIcons( swiperEl );

	const swiper = new SwiperCtor( swiperEl, {
		slidesPerView: 1,
		spaceBetween: 20,
		watchOverflow: true,
		observer: true,
		observeParents: true,
		resizeObserver: true,
		navigation: {
			nextEl: swiperEl.querySelector( '.swiper-button-next' ),
			prevEl: swiperEl.querySelector( '.swiper-button-prev' ),
		},
		breakpoints: {
			[ TABLET_MIN ]: {
				slidesPerView: 2,
				spaceBetween: 20,
			},
			[ DESKTOP_MIN ]: {
				slidesPerView: 3,
				spaceBetween: 20,
			},
		},
		on: {
			init( sw ) {
				requestAnimationFrame( () => sw.update() );
			},
		},
	} );

	swiperEl.btsSwiperInstance = swiper;
	return swiper;
}

function setupImageCardSliders() {
	if ( typeof window.Swiper === 'undefined' ) {
		return;
	}

	document.querySelectorAll( SLIDER_SELECTOR ).forEach( ( container ) => {
		if ( ! isSliderContainer( container ) ) {
			return;
		}

		let swiperEl = getSwiperElement( container );

		if ( ! swiperEl ) {
			swiperEl = wrapCardsAsSwiper( container );
		}

		if ( swiperEl.btsSwiperInstance ) {
			swiperEl.btsSwiperInstance.update();
			return;
		}

		createSwiper( swiperEl );
	} );
}

export default function initImageCardSlider() {
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', setupImageCardSliders );
	} else {
		setupImageCardSliders();
	}

	window.addEventListener( 'load', setupImageCardSliders, { once: true } );
}
